import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-202" data-mp="2i3yzqdh374er">
      {open ? <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-203" data-mp="2i50otluxtrfh">
          <motion.div className="absolute inset-0 bg-navy-900/45" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onClick={onClose} aria-hidden="true" data-id="element-204" data-mp="2n1hdqi9yo9tm" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} initial={{
        opacity: 0,
        y: 16,
        scale: 0.99
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.99
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg rounded-t-2xl bg-white shadow-raised outline-none sm:rounded-2xl" data-id="element-205" data-mp="1zbmzpxdwof7s">
            <div className="flex items-start gap-4 border-b border-navy-100 px-5 py-4" data-id="element-206" data-mp="1kf5nz51t0ykp">
              <div className="min-w-0 flex-1" data-id="element-207" data-mp="3n46xtf4ajweo">
                <h2 className="text-base font-semibold text-navy-900" data-id="element-208" data-mp="3ic5q86i2ituz">
                  {title}
                </h2>
                {description ? <p className="mt-1 text-sm text-navy-500" data-id="element-209" data-mp="3ddcfumy2viz1">{description}</p> : null}
              </div>
              <button type="button" onClick={onClose} aria-label="Close dialog" className="-mr-1 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" data-id="element-210" data-mp="38bn2v1raov8b">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-211" data-mp="3w5dim0dz3tdq" />
              </button>
            </div>
            {children ? <div className="max-h-[60vh] overflow-y-auto px-5 py-4" data-id="element-212" data-mp="2knvl3imzjpzn">
                {children}
              </div> : null}
            {footer ? <div className="flex flex-col-reverse gap-2 border-t border-navy-100 px-5 py-4 sm:flex-row sm:justify-end" data-id="element-213" data-mp="2gomwes6syiay">
                {footer}
              </div> : null}
          </motion.div>
        </div> : null}
    </AnimatePresence>, document.body);
}